import React, { useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '@/redux/store'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '@/redux/hooks'
import { getRoomDetails } from '@/redux/asyncThunks/room'
import ROUTES from '@/routes/routes'
import { selectRoomPlayers } from '@/redux/selectors/roomSelector'
import { getTeamNameById } from '@/utils/teamUtils'
import { fetchAdvisorMessageDistribution } from '@/redux/asyncThunks/usageStats'
import { AdvisorDistributionEntry } from '@/redux/types/usageStats'
import AdvisorChatCard from './AdvisorChatCard'
import WebSocketManager from '@/services/websocketManager'
import { getAccessToken } from '@/utils/authUtils'

const wsManager = WebSocketManager.getInstance()

const AdvisorChatMonitoring: React.FC = () => {
  const currentRoom = useSelector((state: RootState) => state.room.currentRoom)
  const roomPlayers = useSelector(selectRoomPlayers)
  const { teams } = useSelector((state: RootState) => state.team)
  const advisorDistribution = useSelector(
    (state: RootState) => state.usageStats.advisorDistribution
  )
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  useEffect(() => {
    if (currentRoom?.roomid) {
      dispatch(fetchAdvisorMessageDistribution(currentRoom.roomid))
    }
  }, [dispatch, currentRoom?.roomid])

  useEffect(() => {
    const jwtToken = getAccessToken()
    if (!currentRoom?.invite_code || !jwtToken || roomPlayers.length === 0) return

    roomPlayers.forEach((player) => {
      if (player.member_id) {
        wsManager.connectToAdvisorMonitoring(jwtToken, currentRoom.invite_code, player.member_id)
      }
    })

    return () => {
      roomPlayers.forEach((player) => {
        if (player.member_id) {
          wsManager.disconnect(`advisor_monitor_${player.member_id}`)
        }
      })
    }
  }, [currentRoom?.invite_code, roomPlayers])

  const handleRefresh = useCallback(() => {
    if (currentRoom?.roomid) {
      dispatch(getRoomDetails(currentRoom.roomid))
      dispatch(fetchAdvisorMessageDistribution(currentRoom.roomid))
    }
  }, [dispatch, currentRoom?.roomid])

  const handleViewAdvisorChat = (playerId: string) => {
    if (currentRoom?.roomid && currentRoom?.invite_code) {
      navigate(
        ROUTES.PROFESSOR.GAME(currentRoom.roomid, {
          playerId: playerId,
          inviteCode: currentRoom.invite_code,
          panel: 'chat',
        })
      )
    }
  }

  const distributionByUserId = useMemo(() => {
    const map = new Map<number, AdvisorDistributionEntry[]>()
    advisorDistribution?.players.forEach((p) => {
      map.set(p.user_id, p.advisor_distribution)
    })
    return map
  }, [advisorDistribution])

  return (
    <div>
      <div className='flex items-center gap-2 mt-8'>
        <h5 className='text-xl font-bold text-dark-blue'>Advisor Chats</h5>
        <div
          onClick={handleRefresh}
          className='bg-transparent cursor-pointer rounded-md p-1'
        >
          <img src='/icons/refresh.svg' alt='Refresh' className='size-5' />
        </div>
      </div>
      <p className='text-lg text-dark-blue'>
        Monitor player-advisor chat sessions.
      </p>

      {roomPlayers.length > 0 ? (
        <div className='grid grid-cols-[repeat(auto-fill,_minmax(224px,_1fr))] xl:grid-cols-5 gap-9 mt-11'>
          {roomPlayers.map((player) => (
            <AdvisorChatCard
              key={player.member_email}
              playerName={player.member_name}
              playerEmail={player.member_email || ''}
              role={getTeamNameById(teams, player.teamId) ?? undefined}
              distribution={
                distributionByUserId.get(Number(player.member_id)) ?? []
              }
              status={currentRoom?.status!}
              onViewChat={() => handleViewAdvisorChat(player.member_id!)}
            />
          ))}
        </div>
      ) : (
        <div className='h-[15.6rem] flex justify-center items-center flex-col gap-2 mt-11'>
          <img
            src='/icons/no-negotiation.svg'
            alt='No Sessions'
            className='h-40 w-40'
          />
          <p className='text-lg text-dark-blue'>No Players in Room</p>
        </div>
      )}
    </div>
  )
}

export default AdvisorChatMonitoring
